/**
 * Create an API to enable for confirmations.
 */
export function createConfirmations<T, U>(
  confirmationCallback?: ConfirmationCallback<T, U>,
): Confirmations<T, U> {
  const confirmations = new Map<keyof T, Function>();
  let callback = confirmationCallback;

  return {
    cancelConfirmation,
    cancelAllConfirmations,
    confirmAction,
    registerConfirmationCallback,
    requestConfirmation,
  };

  /**
   * Cancel a confirmation.
   */
  function cancelConfirmation(type: keyof T): void {
    confirmations.delete(type);
  }

  /**
   * Cancel all in-progress confirmations.
   */
  function cancelAllConfirmations() {
    confirmations.clear();
  }

  /**
   * Confirm an action.
   */
  function confirmAction(type: keyof T): void {
    confirmations.get(type)?.();
    confirmations.delete(type);
  }

  /**
   * Register a new confirmation callback.
   */
  function registerConfirmationCallback(newCallback: ConfirmationCallback<T, U>): void {
    callback = newCallback;
  }

  /**
   * Request confirmation of an action.
   */
  async function requestConfirmation(params: RequestConfirmationParams<T, U>): Promise<void> {
    const { action, data, text, type } = params;

    confirmations.set(type, action);

    // If a callback has been set, use that for requesting confirmation from the client.
    if (callback) {
      return (await callback({ data, text, type }))
        ? confirmAction(type)
        : cancelConfirmation(type);
    }

    // Otherwise, use the window confirmation alert by default.
    // eslint-disable-next-line no-alert
    if (window.confirm(text)) {
      return confirmAction(type);
    }

    cancelConfirmation(type);
  }
}

export interface Confirmations<T, U> {
  cancelConfirmation(type: keyof T): void;
  cancelAllConfirmations(): void;
  confirmAction(type: keyof T): void;
  registerConfirmationCallback(newCallback: ConfirmationCallback<T, U>): void;
  requestConfirmation(params: RequestConfirmationParams<T, U>): void;
}

export type ConfirmationCallback<T, U> = (
  params: ConfirmationCallbackParams<T, U>,
) => Promise<boolean>;

export type ConfirmationCallbackParams<T, U> = Omit<RequestConfirmationParams<T, U>, 'action'>;

interface RequestConfirmationParams<T, U> {
  type: keyof T;
  action: Function;
  text: string;
  data: U;
}
